import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { FixedAssetValueSummary } from '../../interfaces/fixedAsset/fixedAssetValue.interface';
import { humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  summary: FixedAssetValueSummary;
  canManage: boolean;
  onReverse: (eventId: string) => void;
}

const AssetValueHistory: React.FC<Props> = ({ summary, canManage, onReverse }) => (
  <Paper sx={{ p: 3, mb: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 1 }}>Value events</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="fixedAssets.val.summary">
      <Box sx={{ flex: '1 1 160px' }}>
        <Typography variant="caption" color="text.secondary">Gross cost (incl. revaluation)</Typography>
        <Typography variant="body2">{money(summary.grossCost)}</Typography>
      </Box>
      <Box sx={{ flex: '1 1 160px' }}>
        <Typography variant="caption" color="text.secondary">Accumulated depreciation</Typography>
        <Typography variant="body2">{money(summary.accumulatedDepreciation)}</Typography>
      </Box>
      <Box sx={{ flex: '1 1 160px' }}>
        <Typography variant="caption" color="text.secondary">Accumulated impairment</Typography>
        <Typography variant="body2">{money(summary.accumulatedImpairment)}</Typography>
      </Box>
      <Box sx={{ flex: '1 1 160px' }}>
        <Typography variant="caption" color="text.secondary">Carrying amount</Typography>
        <Typography variant="body2">{money(summary.carryingAmount)}</Typography>
      </Box>
      <Box sx={{ flex: '1 1 160px' }}>
        <Typography variant="caption" color="text.secondary">Revaluation reserve</Typography>
        <Typography variant="body2">{money(summary.revaluationReserveBalance)}</Typography>
      </Box>
    </Box>
    {summary.events.length === 0 ? (
      <Typography variant="body2" color="text.secondary">No revaluation or impairment events.</Typography>
    ) : (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" data-help-section="fixedAssets.val.table">
          <TableHead>
            <TableRow>
              <TableCell data-help="fixedAssets.val.col.date">Date</TableCell><TableCell data-help="fixedAssets.val.col.event">Event</TableCell>
              <TableCell data-help="fixedAssets.val.col.before" align="right">Carrying before</TableCell><TableCell data-help="fixedAssets.val.col.after" align="right">Carrying after</TableCell>
              <TableCell data-help="fixedAssets.val.col.reserve" align="right">Reserve</TableCell><TableCell data-help="fixedAssets.val.col.pl" align="right">P&amp;L</TableCell>
              <TableCell data-help="fixedAssets.val.col.journal">Journal</TableCell><TableCell data-help="fixedAssets.val.col.actions" />
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.events.map(ev => (
              <TableRow key={ev.id}>
                <TableCell>{formatDate(ev.eventDate)}</TableCell>
                <TableCell>
                  {humanizeEnum(ev.eventType)}{' '}
                  {ev.reversed && <Chip size="small" label="Reversed" />}
                </TableCell>
                <TableCell align="right">{money(ev.carryingBefore)}</TableCell>
                <TableCell align="right">{money(ev.carryingAfter)}</TableCell>
                <TableCell align="right">{money(ev.reserveDelta)}</TableCell>
                <TableCell align="right">{money(ev.plAmount)}</TableCell>
                <TableCell>
                  {ev.journalEntryId
                    ? <Link data-help="fixedAssets.val.action.je" component={RouterLink} to={`/accounting/journal-entries/${ev.journalEntryId}`}>View JE</Link>
                    : '—'}
                </TableCell>
                <TableCell>
                  {canManage && summary.reversibleEventId === ev.id && (
                    <Button data-help="fixedAssets.val.action.reverse" size="small" color="error" onClick={() => onReverse(ev.id)}>Reverse</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )}
  </Paper>
);

export default AssetValueHistory;
